import * as THREE from "three";
import { AnimationUtils } from "three";
import { findAnimationClip } from "./AnimationConfig.js";
import * as _animCfg from "./AnimationConfig.js";

const AnimationState = {
    IDLE: "IDLE",
    SPEAK: "SPEAK",
    LISTEN: "LISTEN",
    TRANSITIONING: "TRANSITIONING",
    INITIALIZING: "INITIALIZING",
    MANUAL: "MANUAL",
    SPAWN: "SPAWN",
};

export class AnimationController {
    // NEW: toggle tracing on/off globally
    //static enableCallsiteTracing = true;

    constructor(mixer, model) {
        this.mixer = mixer;
        this.model = model;
        this.animations = [];
        this.trimmedAnimations = new Map();
        this.currentAction = null;
        this.animationState = AnimationState.INITIALIZING;
        this.isTTSActive = false;
        this.transitionDuration = 0.5;
        this.currentModelId = null;
        this.bindUIEvents();
        console.log("AnimationController initialized");
    }

    // NEW: tiny helper to format/emit callsite info
    _traceCallsite(methodName, note = "") {
        if (!AnimationController.enableCallsiteTracing) return;

        // Build a stack and try to pluck the first external caller frame
        const err = new Error();
        const stack = (err.stack || "").split("\n");

        // Chrome-like stacks: [0]Error, [1]this fn, [2]the method, [3]caller.
        // We’ll scan a few frames to find the first one that isn’t our class.
        let callerLine = "";
        for (let i = 2; i < Math.min(stack.length, 10); i++) {
            const line = stack[i] || "";
            if (!line.includes("AnimationController")) {
                // crude but works in dev
                callerLine = line.trim();
                break;
            }
        }

        const tag = `[AnimationController] ${methodName} requested ${note ? ` (source: ${note})` : ""}`;
        console.groupCollapsed(tag);
        if (callerLine) console.log("Caller:", callerLine);
        // clickable frames:
        console.trace("Full call trace:");
        console.groupEnd();

        // Also broadcast for anyone who wants to hook into it
        try {
            const evt = new CustomEvent("animation:trace", {
                detail: { method: methodName, note, stack: err.stack || "" },
            });
            document.dispatchEvent(evt);
        } catch (_) {
            /* ignore if CustomEvent unavailable */
        }
    }

    bindUIEvents() {
        const selector = document.getElementById("animation-selector");
        const playButton = document.getElementById("play-button");
        const stopButton = document.getElementById("stop-button");

        if (selector) {
            selector.addEventListener("change", () => this.onAnimationSelect());
        }
        if (playButton) {
            playButton.addEventListener("click", () =>
                this.playSelectedAnimation(),
            );
        }
        if (stopButton) {
            stopButton.addEventListener("click", () => this.stopAnimation());
        }
    }

    /**
     * Play by known type ("idle","listen","speak","speak2","spawn")
     * or by exact clip name present in the model file.
     * @param {string} typeOrName
     */
    async play(typeOrName) {
        if (!typeOrName) return;
        const t = String(typeOrName).toLowerCase();

        // known types
        if (t === "idle") return this.playIdleAnimation();
        if (t === "listen") return this.playListenAnimation();
        if (t === "speak") return this.playSpeakAnimation();
        if (t === "speak2") return this._playSpeakAnimationByIndex(1);
        if (t === "spawn") return this.playSpawnAnimation();

        // try trimmed by key (if you passed "idle","listen" with different case)
        const trimmed = this.trimmedAnimations.get(t);
        if (trimmed) {
            const act = this.mixer.clipAction(trimmed);
            act.setLoop(THREE.LoopRepeat);
            return this.transitionToAnimation(act, t.toUpperCase());
        }

        // try raw clip by exact name (useful for any custom animation name)
        const raw = this.animations.find(
            (c) => (c.name || "").toLowerCase() === t,
        );
        if (raw) {
            const act = this.mixer.clipAction(raw);
            act.setLoop(THREE.LoopRepeat);
            return this.transitionToAnimation(act, "MANUAL");
        }

        console.warn(`[AnimationController] Unknown animation "${typeOrName}"`);
    }

    /**
     * Infer the authored frame rate of an AnimationClip from its track times.
     * Three.js doesn't store fps on AnimationClip, so AnimationUtils.subclip
     * defaults to fps=30. If the source FBX was authored at 24/60/120, the
     * frame ranges in AnimationConfig get sliced to the wrong time window
     * and the trimmed clip plays back truncated. We sample the densest track
     * and round to the nearest common authored rate.
     */
    _inferClipFps(clip) {
        const FALLBACK = 30;
        if (!clip || !Array.isArray(clip.tracks) || clip.tracks.length === 0) {
            return FALLBACK;
        }
        // Pick the track with the most samples — that's the one whose key
        // density best reflects the authored fps. Skip tracks with < 2 keys.
        let best = null;
        for (const track of clip.tracks) {
            const times = track && track.times;
            if (!times || times.length < 2) continue;
            if (!best || times.length > best.length) best = times;
        }
        if (!best) return FALLBACK;
        const span = best[best.length - 1] - best[0];
        if (span <= 0) return FALLBACK;
        const raw = (best.length - 1) / span;
        // Snap to common authored rates so a tiny sampling jitter doesn't
        // give us 29.97 instead of 30.
        const COMMON = [24, 25, 30, 48, 50, 60, 90, 120];
        let snapped = COMMON[0];
        let bestDelta = Math.abs(raw - COMMON[0]);
        for (let i = 1; i < COMMON.length; i++) {
            const d = Math.abs(raw - COMMON[i]);
            if (d < bestDelta) {
                bestDelta = d;
                snapped = COMMON[i];
            }
        }
        // Only snap when the measured rate is plausibly close (< 10%); else
        // round to integer to avoid forcing a wildly wrong common rate.
        if (bestDelta / Math.max(raw, 1) < 0.1) return snapped;
        return Math.max(1, Math.round(raw));
    }

    // ── Server playback policy (Task #300 follow-on) ────────────────────────
    // Set by ModelViewer from /api/animations/<uuid> BEFORE setupAnimations.
    // No policy (the default everywhere) = behavior identical to before.
    setPlaybackPolicy(policy) {
        this._policy = policy && typeof policy === "object" ? policy : null;
        if (this._policy) {
            console.log("[AnimPolicy] active:", JSON.stringify(this._policy));
        }
    }

    /**
     * Effective fps precedence (Task #326): server override fps → bundled
     * config fps (AnimationConfig[modelId].fps) → inferred from the clip's
     * key density. A declared rate pins the frame-space the ranges were
     * authored in, so a designer re-export at a different keys-per-second
     * rate can't silently shift every range.
     */
    _effectiveFps(clip) {
        try {
            const { getRuntimeAnimationOverride } = _animCfg;
            const o = getRuntimeAnimationOverride(this.currentModelId);
            if (o && Number.isFinite(o.fps) && o.fps >= 1 && o.fps <= 120) {
                return o.fps;
            }
        } catch (_) {}
        try {
            const { getConfiguredFps } = _animCfg;
            const f =
                typeof getConfiguredFps === "function"
                    ? getConfiguredFps(this.currentModelId)
                    : null;
            if (Number.isFinite(f)) return f;
        } catch (_) {}
        return this._inferClipFps(clip);
    }

    /**
     * Build the chained idle sequence (idle A → idle B → … , looped) when the
     * server policy asks for one. Each segment is trimmed from the SAME source
     * clip the normal idle uses, played LoopOnce, and chained via the mixer's
     * "finished" event — only while the state is IDLE, so speak/listen/spawn
     * are completely unaffected.
     */
    _buildIdleSequence(animations, modelId) {
        this._idleSeqClips = null;
        const seq = this._policy && this._policy.idleSequence;
        if (!Array.isArray(seq) || seq.length === 0) return;

        const idleConfig = findAnimationClip(animations, modelId, "idle");
        const source = (idleConfig && idleConfig.clip) || animations[0];
        if (!source) return;

        const fps = this._effectiveFps(source);
        const clips = [];
        for (let i = 0; i < seq.length; i++) {
            const pair = seq[i];
            const a = Array.isArray(pair) ? pair[0] : pair && pair.start;
            const b = Array.isArray(pair) ? pair[1] : pair && pair.end;
            if (!Number.isFinite(a) || !Number.isFinite(b) || a >= b) continue;
            try {
                clips.push(
                    AnimationUtils.subclip(source, `idle_seq_${i}`, a, b, fps),
                );
            } catch (e) {
                console.warn(`[AnimPolicy] idle segment ${i} failed:`, e);
            }
        }
        if (!clips.length) return;
        this._idleSeqClips = clips;
        console.log(
            `[AnimPolicy] idle sequence ready: ${seq.map((p) => `${p[0]}-${p[1]}`).join(" → ")} (fps=${fps}, looped)`,
        );
        if (!this._idleSeqBound) {
            this._idleSeqBound = true;
            this.mixer.addEventListener("finished", (e) =>
                this._onIdleSeqFinished(e),
            );
        }
    }

    _playIdleSeqSegment(index) {
        const clips = this._idleSeqClips;
        if (!clips || !clips.length) return this.playIdleAnimation();
        const i = ((index % clips.length) + clips.length) % clips.length;
        const action = this.mixer.clipAction(clips[i]);
        if (clips.length === 1) {
            // A single-window "sequence" is just a looped idle. Chaining it
            // LoopOnce would wrap onto the SAME cached action through the
            // same-action restart path, and fading in the only active action
            // ramps total blend weight 0→1 — three.js fills the missing
            // weight from the bind pose, dipping the character toward the
            // A-pose at every wrap (fisher/euclid/eratosthenes, idle-seq ×1).
            // LoopRepeat wraps seamlessly and never emits "finished".
            action.setLoop(THREE.LoopRepeat, Infinity);
            action.clampWhenFinished = false;
        } else {
            action.setLoop(THREE.LoopOnce);
            action.clampWhenFinished = true;
        }
        this._idleSeqPos = i;
        this._idleSeqAction = action;
        return this.transitionToAnimation(action, AnimationState.IDLE);
    }

    _onIdleSeqFinished(e) {
        if (!this._idleSeqClips || !this._idleSeqClips.length) return;
        if (this.animationState !== AnimationState.IDLE) return;
        if (!e || e.action !== this._idleSeqAction) return;
        this._playIdleSeqSegment((this._idleSeqPos || 0) + 1);
    }

    /** Optional helper */
    getAvailable() {
        const trimmed = Array.from(this.trimmedAnimations.keys()); // e.g. ["idle","listen","speak","speak2","spawn"]
        const raw = this.animations.map((a) => a.name).filter(Boolean);
        return { trimmed, raw };
    }

    async setupAnimations(animations, modelId) {
        console.log("Setting up animations in controller for model:", modelId);
        console.log(
            "Available animations:",
            animations.map((a) => a.name),
        );

        this.currentModelId = modelId;

        if (!animations || animations.length === 0) {
            console.error("No animations provided to setup");
            return;
        }

        // Fix animation tracks with invalid bone targets
        //  this.fixAnimationTracks(animations);

        this.animations = animations;
        this.trimmedAnimations.clear(); // Clear existing animations

        // Task #300 follow-on: server-policy idle SEQUENCE (idle A → idle B →
        // …, looped). No-op unless setPlaybackPolicy provided one.
        this._buildIdleSequence(animations, modelId);

        // Create trimmed animations using the configuration.
        // walk/lookup are first-class slots (sheet documents them for most
        // legends) but nothing auto-triggers them yet — they're playable via
        // play("walk")/play("lookup") (admin preview, future behaviors) and
        // only materialize when config/override actually carries a range.
        const types = [
            "idle",
            "listen",
            "speak",
            "speak2",
            "spawn",
            "walk",
            "lookup",
        ];
        for (const type of types) {
            const config = findAnimationClip(
                animations,
                this.currentModelId,
                type,
            );
            if (config) {
                const { clip, subclip } = config;
                const fps = this._effectiveFps(clip);
                const trimmed = AnimationUtils.subclip(
                    clip,
                    `${type}_trimmed`,
                    subclip.start,
                    subclip.end,
                    fps,
                );
                console.log(
                    `Creating trimmed animation for ${type} using clip: ${clip.name} ` +
                        `(fps=${fps}, frames=${subclip.start}-${subclip.end}, ` +
                        `srcDur=${clip.duration.toFixed(2)}s, ` +
                        `trimDur=${trimmed.duration.toFixed(2)}s)`,
                );
                this.trimmedAnimations.set(type, trimmed);
            } else {
                console.warn(`No configuration found for ${type} animation`);
            }
        }

        // Setup UI selector
        const selector = document.getElementById("animation-selector");
        if (selector) {
            selector.innerHTML = "";
            animations.forEach((anim, index) => {
                const option = document.createElement("option");
                option.value = index;
                option.textContent = anim.name || `Animation ${index + 1}`;
                selector.appendChild(option);
            });
            selector.disabled = false;
        }

        if (document.getElementById("play-button")) {
            document.getElementById("play-button").disabled = false;
        }
        if (document.getElementById("stop-button")) {
            document.getElementById("stop-button").disabled = false;
        }

        // Play spawn animation on initial load
        // Add Safari fallback if spawn animation doesn't exist
        await this.playSpawnAnimation();
    }

    async playSpawnAnimation() {
        console.log("Playing spawn animation");

        // Check if we have a trimmed spawn animation (using subclip)
        const trimmedSpawnAnimation = this.trimmedAnimations.get("spawn");

        if (trimmedSpawnAnimation) {
            console.log("Using trimmed spawn animation with subclip settings");

            const spawnAction = this.mixer.clipAction(trimmedSpawnAnimation);
            spawnAction.setLoop(THREE.LoopOnce);
            spawnAction.clampWhenFinished = true;

            this._installSpawnFinishedHandler(spawnAction.getMixer());
            this._spawnFinishedAction = spawnAction;

            await this.transitionToAnimation(spawnAction, AnimationState.SPAWN);
            return;
        }

        // Fallback to original method if no trimmed animation
        const spawnConfig = findAnimationClip(
            this.animations,
            this.currentModelId,
            "spawn",
        );

        if (!spawnConfig) {
            console.warn("No spawn animation found, defaulting to idle");
            
            // Still dispatch the spawn complete event so greeting can trigger
            const spawnCompleteEvent = new CustomEvent(
                "spawnAnimationComplete",
                {
                    detail: { modelId: this.currentModelId },
                },
            );
            document.dispatchEvent(spawnCompleteEvent);
            console.log("Dispatched spawnAnimationComplete event (no spawn animation fallback)");
            
            return this.playIdleAnimation();
        }

        const { clip, subclip } = spawnConfig;
        console.log("Found spawn animation:", clip.name);

        // Create a subclip on the fly if we don't have a pre-trimmed one
        let animationToPlay;
        if (
            subclip &&
            subclip.start !== undefined &&
            subclip.end !== undefined
        ) {
            const fps = this._effectiveFps(clip);
            console.log(
                `Creating spawn subclip from frame ${subclip.start} to ${subclip.end} (fps=${fps})`,
            );
            animationToPlay = AnimationUtils.subclip(
                clip,
                "spawn_subclip",
                subclip.start,
                subclip.end,
                fps,
            );
        } else {
            animationToPlay = clip;
        }

        const spawnAction = this.mixer.clipAction(animationToPlay);
        spawnAction.setLoop(THREE.LoopOnce);
        spawnAction.clampWhenFinished = true;

        this._installSpawnFinishedHandler(spawnAction.getMixer());
        this._spawnFinishedAction = spawnAction;

        await this.transitionToAnimation(spawnAction, AnimationState.SPAWN);
    }

    /**
     * Install a single 'finished' listener on the mixer that runs the
     * spawn-complete pipeline (dispatch event, transition to idle). The
     * old code called mixer.removeEventListener('finished') WITHOUT a
     * handler argument, which is a no-op in Three.js's EventDispatcher,
     * so a new listener was stacked on every spawn play. After enough
     * spawns the same finish event fired the idle transition many times
     * in a row, interrupting whatever was playing. Here we keep a single
     * bound handler instance and properly remove the previous one before
     * adding a new one.
     */
    _installSpawnFinishedHandler(mixer) {
        if (this._spawnFinishedHandler && this._spawnFinishedMixer) {
            this._spawnFinishedMixer.removeEventListener(
                "finished",
                this._spawnFinishedHandler,
            );
        }
        this._spawnFinishedMixer = mixer;
        // Track which AnimationAction the current spawn is wired to so
        // the handler only fires for spawn completions, not for any
        // other LoopOnce action that may share the mixer in the future.
        this._spawnFinishedAction = null;
        this._spawnFinishedHandler = (event) => {
            if (
                this._spawnFinishedAction
                && event
                && event.action !== this._spawnFinishedAction
            ) {
                return;
            }
            console.log("Spawn animation finished, transitioning to idle");
            const spawnCompleteEvent = new CustomEvent(
                "spawnAnimationComplete",
                { detail: { modelId: this.currentModelId } },
            );
            document.dispatchEvent(spawnCompleteEvent);
            this.playIdleAnimation();
        };
        mixer.addEventListener("finished", this._spawnFinishedHandler);
    }

    async playIdleAnimation() {
        //this._traceCallsite("playIdleAnimation", note);
        if (this.animationState === AnimationState.INITIALIZING) {
            console.log("Cannot play idle animation while initializing");
            return;
        }

        /*if (this.isTTSActive) {
            console.log("Skipping idle animation - TTS is active");
            return;
        }*/

        console.log("Attempting to play idle animation");
        console.log(
            "Available clips:",
            this.animations.map((a) => a.name),
        );

        // Server playback policy: chained idle sequence (idle A → idle B → …).
        if (this._idleSeqClips && this._idleSeqClips.length) {
            return this._playIdleSeqSegment(0);
        }

        // First try to use the trimmed animation
        const trimmedIdleAnimation = this.trimmedAnimations.get("idle");

        if (trimmedIdleAnimation) {
            console.log("Using trimmed idle animation with subclip settings");
            const idleAction = this.mixer.clipAction(trimmedIdleAnimation);
            idleAction.setLoop(THREE.LoopRepeat);
            await this.transitionToAnimation(idleAction, AnimationState.IDLE);
            return;
        }

        // Fallback to original animation
        console.log("Looking for animation type:", "idle");
        console.log("Model ID:", this.currentModelId);
        const idleConfig = findAnimationClip(
            this.animations,
            this.currentModelId,
            "idle",
        );

        if (!idleConfig) {
            console.error("No idle animation found");
            return;
        }

        console.log("Found idle animation:", idleConfig.clip.name);
        const idleAction = this.mixer.clipAction(idleConfig.clip);
        idleAction.setLoop(THREE.LoopRepeat);
        await this.transitionToAnimation(idleAction, AnimationState.IDLE);
    }

    async playListenAnimation() {
        if (this.animationState === AnimationState.INITIALIZING) {
            console.log("Cannot play listen animation while initializing");
            return;
        }

        /*if (this.isTTSActive || this.animationState === AnimationState.LISTEN) {
            console.log('Skipping listen animation - TTS active or already listening');
            return;
        }*/

        console.log("Playing listen animation");
        const listenClip = this.trimmedAnimations.get("listen");

        if (!listenClip) {
            console.error("No trimmed listen animation found");
            return;
        }

        const listenAction = this.mixer.clipAction(listenClip);
        listenAction.setLoop(THREE.LoopRepeat);
        await this.transitionToAnimation(listenAction, AnimationState.LISTEN);
        console.log("Listen animation started");
    }

    async playSpeakAnimation() {
        if (this.animationState === AnimationState.INITIALIZING) {
            console.log("Cannot play speak animation while initializing");
            return;
        }

        /*if (this.animationState === AnimationState.SPEAK) {
            console.log('Already in speak animation state, checking for animation switch');
            
            // Check if we need to switch between speak animations
            this._alternateSpeakAnimations();
            return;
        }*/

        console.log("Initiating speak animation sequence");
        // Server playback policy may lock the variant ("play only speak2").
        const speakMode = (this._policy && this._policy.speak) || "alternate";
        this.currentSpeakAnimationIndex =
            speakMode === "speak2" && this.trimmedAnimations.get("speak2")
                ? 1
                : 0;
        this._playSpeakAnimationByIndex(this.currentSpeakAnimationIndex);
    }

    /**
     * Play a specific speak animation by index
     * @param {number} index - 0 for speak, 1 for speak2
     */
    async _playSpeakAnimationByIndex(index) {
        const animType = index === 0 ? "speak" : "speak2";
        console.log(`Playing ${animType} animation`);

        // Get the appropriate animation clip
        const speakClip = this.trimmedAnimations.get(animType);

        if (!speakClip) {
            console.error(
                `No trimmed ${animType} animation found, falling back to default speak`,
            );
            // If speak2 isn't available, try using speak instead
            if (animType === "speak2") {
                const fallbackClip = this.trimmedAnimations.get("speak");
                if (fallbackClip) {
                    const fallbackAction = this.mixer.clipAction(fallbackClip);
                    fallbackAction.setLoop(THREE.LoopRepeat);
                    fallbackAction.timeScale = 0.8;
                    await this.transitionToAnimation(
                        fallbackAction,
                        AnimationState.SPEAK,
                    );
                    return;
                }
            }
            return;
        }

        const speakAction = this.mixer.clipAction(speakClip);
        speakAction.setLoop(THREE.LoopRepeat);
        speakAction.timeScale = 0.8;
        await this.transitionToAnimation(speakAction, AnimationState.SPEAK);

        // Schedule next animation switch (skipped when the server policy locks
        // playback to a single speak variant — nothing to alternate to).
        const mode = (this._policy && this._policy.speak) || "alternate";
        if (mode === "alternate") {
            this.scheduleSpeakSwitch();
        }
    }

    /**
     * Schedule switching between speak animations
     * Uses more intelligent timing based on animation and speech patterns
     */
    scheduleSpeakSwitch() {
        // Clear any existing timer
        if (this.speakSwitchTimer) {
            clearTimeout(this.speakSwitchTimer);
        }

        // If we have a current action, wait until the current animation cycle completes
        if (this.currentAction) {
            // Get animation cycle information
            const duration = this.currentAction._clip.duration;
            const currentTime = this.currentAction.time;

            // Calculate time remaining in the current animation cycle
            // We want to switch exactly at the end of the current animation cycle
            const cycleProgress = (currentTime % duration) / duration;
            const timeRemainingInCycle = duration * (1.0 - cycleProgress);

            // Convert to milliseconds with a small buffer (10ms) to ensure we're past the cycle
            const switchDelay = timeRemainingInCycle * 1000 + 10;

            console.log(
                `Animation cycle at ${(cycleProgress * 100).toFixed(1)}%, switching to next speak animation in ${switchDelay.toFixed(2)}ms when current cycle completes`,
            );

            // Set timer to switch animations exactly when the current cycle completes
            this.speakSwitchTimer = setTimeout(() => {
                if (this.animationState === AnimationState.SPEAK) {
                    this._alternateSpeakAnimations();
                }
            }, switchDelay);
        } else {
            // Fallback if no current action (shouldn't happen, but adding for robustness)
            console.log(
                "No current action found, using default timing for speak animation switch",
            );
            this.speakSwitchTimer = setTimeout(() => {
                if (this.animationState === AnimationState.SPEAK) {
                    this._alternateSpeakAnimations();
                }
            }, 2000); // Default 2-second delay as fallback
        }
    }

    /**
     * Alternate between speak animations
     */
    _alternateSpeakAnimations() {
        if (this.animationState !== AnimationState.SPEAK) {
            return;
        }

        // Server playback policy: locked to one variant — never alternate.
        const mode = (this._policy && this._policy.speak) || "alternate";
        if (mode !== "alternate") {
            return;
        }

        // Toggle between 0 and 1
        this.currentSpeakAnimationIndex =
            this.currentSpeakAnimationIndex === 0 ? 1 : 0;

        // See if speak2 animation exists
        if (
            this.currentSpeakAnimationIndex === 1 &&
            !this.trimmedAnimations.get("speak2")
        ) {
            console.log(
                "Speak2 animation not available, staying with speak animation",
            );
            this.currentSpeakAnimationIndex = 0;
            this.scheduleSpeakSwitch();
            return;
        }

        // Play the next animation
        this._playSpeakAnimationByIndex(this.currentSpeakAnimationIndex);
    }

    /**
     * True when any OTHER enabled action on the mixer currently carries
     * blend weight. Used by the same-action restart path: fading in the
     * sole active action leaves total weight < 1 mid-ramp, and three.js
     * blends the missing weight from the bind pose (the A-pose dip).
     * Defensive over three.js internals — on any doubt it says false,
     * which restarts at full weight (worst case a pose cut, never a
     * bind-pose dip).
     */
    _othersCarryWeight(action) {
        try {
            const actions = this.mixer._actions;
            if (!Array.isArray(actions)) return false;
            for (const other of actions) {
                if (other === action || !other.enabled) continue;
                if (
                    typeof this.mixer._isActiveAction === "function" &&
                    !this.mixer._isActiveAction(other)
                )
                    continue;
                if (other.getEffectiveWeight() > 0.001) return true;
            }
        } catch (_) {
            /* fall through to the safe default */
        }
        return false;
    }

    async transitionToAnimation(action, newState) {
        // Fast path: same action already running. No transition needed.
        if (this.animationState === newState && this.currentAction === action) {
            if (action.isRunning()) {
                console.log(
                    "Already playing the same animation, no transition needed",
                );
                return;
            }
            // Same action exists but was stopped (e.g., by stopAllAction).
            // Only fade it back in when some OTHER action still carries
            // weight (a real blend partner). If it is the ONLY active
            // action, fadeIn would ramp total weight 0→1 and three.js
            // blends the gap from the bind pose — the A-pose dip. Restart
            // at full weight instead.
            const fadeRestart = this._othersCarryWeight(action);
            console.log(
                `[Transition] ${this.animationState}→${newState} dur=${this.transitionDuration}s mode=restart${fadeRestart ? "" : " fullWeight"}`,
            );
            action.reset().setEffectiveTimeScale(1).setEffectiveWeight(1);
            if (fadeRestart) action.fadeIn(this.transitionDuration);
            action.play();
            this.currentAction = action;
            this.animationState = newState;
            return;
        }

        // Speak↔speak2 swaps run shorter so the cycle-boundary seam is hidden.
        const isSpeakTransition =
            this.animationState === AnimationState.SPEAK &&
            newState === AnimationState.SPEAK &&
            this.currentAction !== action;
        const transitionDuration = isSpeakTransition
            ? this.transitionDuration / 3
            : this.transitionDuration;

        const previousAction = this.currentAction;
        const fromState = this.animationState;

        // If a previous transition is still in its tail (waiting on the
        // outgoing action's stop timer), cancel that timer and stop the
        // retired action immediately. The mixer will blend the new pair
        // cleanly from whatever weight the outgoing action currently has.
        if (this._pendingFadeOutTimer) {
            clearTimeout(this._pendingFadeOutTimer);
            this._pendingFadeOutTimer = null;
        }
        if (this._pendingFadeOutAction
            && this._pendingFadeOutAction !== previousAction
            && this._pendingFadeOutAction !== action) {
            this._pendingFadeOutAction.stop();
        }
        this._pendingFadeOutAction = null;

        if (previousAction && previousAction !== action) {
            // Mixer-driven crossfade. The incoming action must be playing
            // before crossFadeTo is called; warp=true aligns time-scales so
            // looping clips don't visibly speed-bump during the blend.
            action.reset().setEffectiveTimeScale(1).setEffectiveWeight(1).play();
            previousAction.crossFadeTo(action, transitionDuration, true);

            console.log(
                `[Transition] ${fromState}→${newState} dur=${transitionDuration}s mode=crossfade${isSpeakTransition ? " speakSwap" : ""}`,
            );

            // Stop the retired action shortly after the crossfade completes
            // so its weight doesn't drift and so it stops consuming mixer
            // bandwidth. A small slack guards against the mixer not having
            // ticked exactly at the target time.
            this._pendingFadeOutAction = previousAction;
            this._pendingFadeOutTimer = setTimeout(() => {
                if (this._pendingFadeOutAction === previousAction
                    && this.currentAction !== previousAction) {
                    previousAction.stop();
                }
                this._pendingFadeOutAction = null;
                this._pendingFadeOutTimer = null;
            }, transitionDuration * 1000 + 50);
        } else {
            // First animation, or same action that wasn't current. Just play.
            console.log(
                `[Transition] ${fromState}→${newState} dur=${transitionDuration}s mode=fadeIn`,
            );
            action
                .reset()
                .setEffectiveTimeScale(1)
                .setEffectiveWeight(1)
                .fadeIn(transitionDuration)
                .play();
        }

        this.currentAction = action;
        this.animationState = newState;
    }

    onTTSStart() {
        console.log("TTS started - activating speak animation sequence");
        this.isTTSActive = true;
        // Don't start animation yet, wait for audio playback
    }

    onTTSEnd() {
        console.log("TTS ended - initiating transition to idle animation");
        this.isTTSActive = false;
        this.playIdleAnimation();
    }

    onAudioPlaybackStart() {
        console.log("Audio playback started - initiating speak animation");
        if (this.isTTSActive) {
            this.playSpeakAnimation();
        }
    }

    async playAnimation() {
        if (
            !this.currentAction ||
            this.isTTSActive ||
            this.animationState === AnimationState.INITIALIZING
        )
            return;
        await this.currentAction.reset().fadeIn(this.transitionDuration).play();
    }

    async stopAnimation() {
        if (
            !this.currentAction ||
            this.isTTSActive ||
            this.animationState === AnimationState.INITIALIZING
        )
            return;
        await this.currentAction.fadeOut(this.transitionDuration);
        if (!this.isTTSActive) {
            await this.playIdleAnimation();
        }
    }

    async onAnimationSelect() {
        if (
            this.isTTSActive ||
            this.animationState === AnimationState.INITIALIZING
        ) {
            console.log(
                "Skipping animation select - TTS is active or system is initializing",
            );
            return;
        }

        const selector = document.getElementById("animation-selector");
        const selectedIndex = parseInt(selector.value);

        if (selectedIndex >= 0 && this.mixer) {
            const animation = this.animations[selectedIndex];
            const action = this.mixer.clipAction(animation);
            action.setLoop(THREE.LoopRepeat);
            action.clampWhenFinished = false;
            await this.transitionToAnimation(action, AnimationState.MANUAL);
        }
    }

    /**
     * Handle immediate interruption with guaranteed transition to listening animation
     * This method ensures listening animation ALWAYS plays regardless of current animation state
     */
    async handleInterruptionTransition() {
        if (this.animationState === AnimationState.INITIALIZING) {
            console.log(
                "Cannot handle interruption transition while initializing",
            );
            return;
        }

        console.log(
            `🚨 FORCE INTERRUPTION: Transitioning from ${this.animationState} to LISTEN (guaranteed)`,
        );

        /* // Clear any scheduled speak animation switches
        if (this.speakSwitchTimer) {
            clearTimeout(this.speakSwitchTimer);
            this.speakSwitchTimer = null;
            console.log(
                "🛑 Cleared speak animation switch timer during interruption",
            );
        }

        // Clear any ongoing transition promises to prevent conflicts
        if (this.transitionPromise) {
            console.log("🛑 Interrupting ongoing animation transition");
            this.transitionPromise = null;
        }*/

        // Force stop TTS flag to allow animation transitions
        this.isTTSActive = false;

        try {
            // Get listening animation clip - this is mandatory for interruptions
            const listenClip = this.trimmedAnimations.get("listen");
            if (!listenClip) {
                console.error(
                    "❌ CRITICAL: No trimmed listen animation found - using fallback",
                );
                // Try to create listening animation from original animations
                const listenConfig = findAnimationClip(
                    this.animations,
                    this.currentModelId,
                    "listen",
                );
                if (listenConfig) {
                    console.log(
                        "🔄 Creating listening animation from config as fallback",
                    );
                    const { clip } = listenConfig;
                    const listenAction = this.mixer.clipAction(clip);
                    listenAction.setLoop(THREE.LoopRepeat);
                    await this.forceTransitionToAnimation(
                        listenAction,
                        AnimationState.LISTEN,
                    );
                    return;
                } else {
                    console.error(
                        "❌ CRITICAL: No listening animation available at all",
                    );
                    return this.playIdleAnimation();
                }
            }

            console.log(
                `🎬 FORCING transition to LISTEN from current state: ${this.animationState}`,
            );

            const listenAction = this.mixer.clipAction(listenClip);
            listenAction.setLoop(THREE.LoopRepeat);

            // Use ultra-fast transition for interruptions (immediate response)
            await this.forceTransitionToAnimation(
                listenAction,
                AnimationState.LISTEN,
            );

            console.log(
                "👂 GUARANTEED listening animation activated after interruption",
            );
        } catch (error) {
            console.error(
                "❌ Error during forced interruption transition:",
                error,
            );

            // Emergency fallback - directly set listening animation without transition
            try {
                console.log(
                    "🚨 EMERGENCY: Direct listening animation activation",
                );
                const listenClip = this.trimmedAnimations.get("listen");
                if (listenClip) {
                    // Stop current animation immediately
                    if (this.currentAction) {
                        this.currentAction.stop();
                    }

                    // Start listening animation directly
                    const listenAction = this.mixer.clipAction(listenClip);
                    listenAction.setLoop(THREE.LoopRepeat);
                    listenAction.reset();
                    listenAction.setEffectiveWeight(1);
                    listenAction.play();

                    this.currentAction = listenAction;
                    this.animationState = AnimationState.LISTEN;

                    console.log(
                        "🚨 EMERGENCY listening animation activated successfully",
                    );
                } else {
                    console.error(
                        "❌ EMERGENCY FALLBACK: Using idle animation",
                    );
                    await this.playIdleAnimation();
                }
            } catch (emergencyError) {
                console.error(
                    "❌ CRITICAL ERROR in emergency listening animation:",
                    emergencyError,
                );
            }
        }
    }

    /**
     * Force transition to animation without waiting for current transitions
     * Used specifically for interruption scenarios where immediate response is required
     */
    async forceTransitionToAnimation(action, newState) {
        console.log(
            `🚨 FORCE TRANSITION from ${this.animationState} to ${newState}`,
        );

        // Cancel any in-flight crossfade tail so the retired action doesn't
        // get stopped twice and so the timer doesn't fire after we've moved on.
        if (this._pendingFadeOutTimer) {
            clearTimeout(this._pendingFadeOutTimer);
            this._pendingFadeOutTimer = null;
        }
        if (this._pendingFadeOutAction
            && this._pendingFadeOutAction !== action) {
            this._pendingFadeOutAction.stop();
        }
        this._pendingFadeOutAction = null;

        // Force stop any current action immediately
        if (this.currentAction) {
            this.currentAction.stop();
        }

        // Start new animation immediately
        action.reset();
        action.setEffectiveWeight(1);
        action.play();

        this.currentAction = action;
        this.animationState = newState;

        console.log(`🚨 FORCE TRANSITION complete: Now in ${newState} state`);
    }
}
